import {
  NotificationType,
  type PanelResolveType,
  type PageRef,
} from '@wix/editor-platform-sdk-types'
import {
  getAssetUrlInWorker,
  getPatternDefForPage,
  parseUrl,
} from '@wix/wix-data-client-common'
import type { RouterConfig, AppApi } from '@wix/wix-data-client-common'
import {
  type EditorSDK,
  type I18n,
} from '@wix/wix-data-client-common-standalone'

export const openConfirmPatternChangePanel = async ({
  editorSdkProxy,
  i18n,
  pageCount,
}: {
  editorSdkProxy: EditorSDK
  i18n: I18n
  pageCount: number
}): Promise<PanelResolveType> =>
  editorSdkProxy.editor.openErrorPanel({
    headerText: i18n.t(
      'EditDynamicPageUrlStructure_ReplaceUrlsConfirmation_Header',
    ),
    illustration: await getAssetUrlInWorker(
      editorSdkProxy,
      'panel-icons/disconnect_dynamic_page.svg',
    ),
    shouldShowIllustration: true,
    topDescriptionText: i18n.t(
      'EditDynamicPageUrlStructure_ReplaceUrlsConfirmation_Description',
      { count: pageCount },
    ),
    mainActionText: i18n.t(
      'EditDynamicPageUrlStructure_ReplaceUrlsConfirmation_MainActionText',
    ),
    secondaryActionText: i18n.t(
      'EditDynamicPageUrlStructure_ReplaceUrlsConfirmation_SecondaryActionText',
    ),
    helpId: '0f65b919-6ef2-4601-94df-4a526a5b4a93',
  })

export const getUrlParts = async ({
  editorSdkProxy,
  pageRef,
  newUrl,
}: {
  editorSdkProxy: EditorSDK
  pageRef: PageRef
  newUrl: string
}): Promise<{
  previousPrefix: string
  previousPattern: string
  newPrefix: string
  newPattern: string
}> => {
  const routerRef = await editorSdkProxy.routers.getByPage({ pageRef })
  const router = await editorSdkProxy.routers.get<RouterConfig>({ routerRef })
  const { pattern: previousPattern } = getPatternDefForPage(
    router.config,
    router.pages,
    pageRef,
  )
  const { pattern: newPattern, prefix: newPrefix } = parseUrl(newUrl)

  return {
    previousPrefix: router.prefix,
    previousPattern,
    newPrefix,
    newPattern,
  }
}

export const showSuccessToastNotification = ({
  editorSdkProxy,
  appApi,
  i18n,
  pageCount,
  shouldShowLink,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  i18n: I18n
  pageCount: number
  shouldShowLink: boolean
}): void => {
  const message = i18n.t(
    'EditDynamicPageUrlStructure_SuccessNotification_WithoutRedirect',
    { count: pageCount },
  )

  const linkText = shouldShowLink
    ? i18n.t('EditDynamicPageUrlStructure_SuccessNotification_LearnMoreLink')
    : undefined

  const onClick = shouldShowLink
    ? () => {
        editorSdkProxy.editor.openHelpPanel({
          helpId: '0f65b919-6ef2-4601-94df-4a526a5b4a93',
        })
      }
    : undefined

  appApi.showToastNotification(
    {
      message,
      type: NotificationType.Success,
      linkText,
    },
    onClick,
  )
}

export const showUrlChangeErrorPanel = async ({
  editorSdkProxy,
  i18n,
}: {
  editorSdkProxy: EditorSDK
  i18n: I18n
}): Promise<PanelResolveType> =>
  editorSdkProxy.editor.openErrorPanel({
    headerText: i18n.t(
      'EditDynamicPageUrlStructure_UrlUpdateErrorPanel_Header',
    ),
    illustration: await getAssetUrlInWorker(
      editorSdkProxy,
      'panel-icons/disconnect_dynamic_page.svg',
    ),
    shouldShowIllustration: true,
    topDescriptionText: i18n.t(
      'EditDynamicPageUrlStructure_UrlUpdateErrorPanel_Description',
    ),
    mainActionText: i18n.t(
      'EditDynamicPageUrlStructure_UrlUpdateErrorPanel_MainActionText',
    ),
    secondaryActionText: i18n.t(
      'EditDynamicPageUrlStructure_UrlUpdateErrorPanel_SecondaryActionText',
    ),
  })
